import React, { useEffect, ReactNode } from 'react';
import { analyticsService } from '../services/analyticsService';

interface AnalyticsProviderProps {
  children: ReactNode;
}

/**
 * Analytics Provider Component
 * Wraps the app to enable analytics tracking and event listeners
 */
export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  useEffect(() => {
    // page_view is sent by gtag('config') in index.html; sending it here too double-counted views.

    // Track when user leaves (session end)
    const handleBeforeUnload = () => {
      analyticsService.endSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Track errors globally
    const handleError = (event: ErrorEvent) => {
      analyticsService.trackError({
        errorType: event.error?.name || 'UnknownError',
        errorMessage: event.message,
        stackTrace: event.error?.stack,
      });
    };

    window.addEventListener('error', handleError);

    // Track unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      analyticsService.trackError({
        errorType: 'UnhandledPromiseRejection',
        errorMessage: event.reason?.message || String(event.reason),
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return <>{children}</>;
};

export default AnalyticsProvider;
